/**
 * User preferences — the terminal's settings store.
 *
 * Distinct from the two neighbouring kinds of state, and the distinction is
 * what makes this safe to persist:
 *  - A session's CURRENT model lives on the gateway and is written with
 *    `sessions.patch { model }`. It is not a preference and is deliberately
 *    not stored locally (see the `selectedModel` key sweep in the react
 *    package's `buildTerminalStores`).
 *  - `HistoryUiState` is incidental view state (which folders are collapsed,
 *    which session was last open) — per-device by nature.
 * What lands here is what the user explicitly chose and would expect to still
 * be true tomorrow, and on their other machine.
 *
 * Merge model: field-level last-write-wins. A whole-doc LWW would silently
 * drop one of "laptop sets the theme, desktop sets the default model" — two
 * edits that never actually conflict. So each field carries its own ms stamp
 * and merges independently; only genuine same-field edits race, and there the
 * later write wins. Stamps are wall-clock, so a badly skewed device can lose
 * a write it "should" have won — acceptable for preferences, where the cost is
 * one re-click, and far cheaper than the vector clock the alternative needs.
 */

/**
 * Colour scheme. `system` follows the OS via `prefers-color-scheme`; the
 * other two pin it. Applying this is the HOST's job (it owns the CSS
 * variables the components style against) — the terminal only records it.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

/**
 * UI scale, as a percentage of the shell's natural size. Applying it is the
 * HOST's job for the same reason the theme is: an embed must not be able to
 * resize its host page.
 *
 * A closed set rather than a free number. The control is a detented slider, so
 * only these values are reachable anyway, and pinning the set means a doc from
 * another device (or a newer build) can be validated rather than trusted — a
 * synced `uiScale: 0.01` would otherwise render the app unusable on arrival,
 * with the control needed to undo it too small to hit.
 */
export const UI_SCALES = [80, 90, 100, 110, 125, 150] as const;
export type UiScale = (typeof UI_SCALES)[number];

/**
 * Which font stack the shell renders in, as a stable id rather than a CSS
 * font-family string: the id is what syncs, so the actual stack can be
 * retuned (or given better fallbacks) without rewriting stored docs, and a
 * stored value can never inject arbitrary CSS into the host's stylesheet.
 *
 * System-installed families only — no webfont loading. A font the user's OS
 * lacks falls through its stack, which is why each id maps to a stack rather
 * than a single family.
 */
export type FontPreference = 'default' | 'system' | 'serif' | 'mono';

/**
 * The stack each id resolves to. Exported because the HOST applies these and
 * the settings pane previews them — one source of truth, so the sample you
 * click can't drift from the font you get.
 *
 * Every entry ends in a generic family, so a machine missing the named faces
 * still lands somewhere sensible rather than on the browser's default serif.
 */
export const FONT_STACKS: Readonly<Record<FontPreference, string>> = {
  // The shell's own: what the app shipped with before this preference existed.
  default: `'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  // Whatever the OS uses for its own chrome — the closest thing to "native".
  system: `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
  serif: `Georgia, Cambria, 'Times New Roman', Times, serif`,
  mono: `ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`,
};

/**
 * The language the ASSISTANT replies in.
 *
 * Deliberately not the language of the app's own buttons and menus. Those are
 * hardcoded English strings and translating them is a catalogue, a build step
 * and a permanent per-string obligation across every locale — a separate piece
 * of work. This is the other axis, and it is nearly free: the model already
 * speaks these languages, so adding one is a line in the table below with no
 * translation, no bundle cost and nothing to maintain.
 *
 * It also covers most of what a user actually reads. The replies are the
 * paragraphs on screen; the chrome is a few dozen short nouns around them.
 *
 * `auto` sends no instruction at all, which is why it is the default: left to
 * itself the model answers in whatever language you wrote in, and that is the
 * right behaviour for anyone who never opens this setting.
 */
export type LanguagePreference = 'auto' | (string & {});

/**
 * Offered languages, as BCP-47 tag → endonym (the name in the language
 * itself). Endonyms because someone hunting for their own language scans for
 * "Deutsch", not "German" — a list in English is only readable by people who
 * already read English, which is exactly the wrong audience for it.
 */
export const LANGUAGES: Readonly<Record<string, string>> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  ru: 'Русский',
  uk: 'Українська',
  tr: 'Türkçe',
  ar: 'العربية',
  hi: 'हिन्दी',
  'zh-Hans': '简体中文',
  'zh-Hant': '繁體中文',
  ja: '日本語',
  ko: '한국어',
  vi: 'Tiếng Việt',
  th: 'ไทย',
  id: 'Bahasa Indonesia',
};

/**
 * The instruction appended to every turn's framing, or `null` for `auto`.
 *
 * Written in English rather than the target language: the directive is for the
 * model, not the user, and instruction-following is most reliable in the
 * language the surrounding framing already uses.
 *
 * The second sentence is the load-bearing one. Without it the model tends to
 * mirror whatever language the user just typed in, which silently defeats a
 * setting whose whole purpose is to hold a language the user is NOT writing in
 * — a Spanish speaker practising English, or anyone pasting an English log and
 * wanting the explanation back in their own language.
 */
export function languageDirective(language: LanguagePreference): string | null {
  if (language === 'auto') return null;
  const name = LANGUAGES[language];
  if (name == null) return null;
  return (
    `Respond in ${name}. Keep replying in ${name} even when the user writes ` +
    `in another language, unless they explicitly ask you to switch.`
  );
}

/**
 * Every directive this app can prepend, for the reverse direction: hydration
 * from `chat.history` has to take the framing back OFF a stored user turn,
 * and a chat carries whichever directive was current when each turn was sent.
 * Stripping only today's would leave last week's setting visible in an old
 * bubble, so the reader is given the whole set.
 *
 * WHAT THIS DOES NOT COVER, and it is worth knowing before editing the
 * wording above: the reader matches these strings EXACTLY. So the invariant
 * holds across a change of SETTING and not across a change of WORDING —
 * rewording a directive, or dropping a language from `LANGUAGES`, leaves
 * every turn already sent under the old text unstrippable, and the directive
 * then shows up inside the user's own bubbles forever. Treat these strings
 * as a persisted format: add to them, never edit or remove one. A stable
 * marker on the wire would make that unnecessary and is the real fix if this
 * ever needs to change.
 */
export function allLanguageDirectives(): readonly string[] {
  const out: string[] = [];
  for (const code of Object.keys(LANGUAGES)) {
    const directive = languageDirective(code as LanguagePreference);
    if (directive != null) out.push(directive);
  }
  return out;
}

/**
 * How much of a chat failure this user is willing to have reported. Only the
 * DIAGNOSTIC half (error text, which chat, which run) is optional; outcome,
 * duration and category are always reported.
 *
 *  - `identified` — everything below plus the account id.
 *  - `anonymous` — the DEFAULT. Full detail (scrubbed, never message content)
 *    under a salted hash.
 *  - `minimal` — run outcomes only. No error text, no ids, no account
 *    reference of any kind.
 *
 * `minimal` is deliberately not "off": if the people having the worst
 * experience are the likeliest to opt out, an opt-out-able denominator makes
 * the product look better the worse it gets.
 */
export type TelemetryPreference = 'identified' | 'anonymous' | 'minimal';

export interface TerminalPreferences {
  /**
   * Model every NEW session starts on, as a catalog id (the same string
   * `sessions.patch { model }` takes). `null` ⇒ no explicit default: the terminal falls
   * back to inheriting the last-used model, then the gateway's own default.
   * Not validated here — a stored id can outlive its catalog entry, so the
   * consumer resolves it against the live catalog and ignores it if gone.
   */
  readonly defaultModelId: string | null;
  /**
   * Defaults to `light` rather than `system`: the shell ships a light palette,
   * and defaulting to `system` would silently flip every dark-OS user to a
   * dark theme they never asked for. Dark is opt-in.
   */
  readonly theme: ThemePreference;
  /**
   * Percentage scale for the whole shell. 100 ⇒ untouched.
   *
   * Syncs like every other preference, which is a deliberate call and the
   * arguable one: scale is partly a property of the DEVICE (a 4K laptop and a
   * 1080p monitor want different values), so a user who scales up on one may
   * not want it on the other. It syncs anyway because the far more common case
   * is a preference about the person — eyesight travels with them — and
   * because the alternative silently strands the setting on one machine.
   */
  readonly uiScale: UiScale;
  /** Font stack for the whole shell. `default` ⇒ the shell's own choice. */
  readonly fontFamily: FontPreference;
  /**
   * Language the assistant replies in. `auto` ⇒ no instruction sent, so the
   * model follows whatever the user wrote in.
   */
  readonly language: LanguagePreference;
  /**
   * How much of a chat failure may be reported. DEVICE-LOCAL — see
   * `DEVICE_LOCAL_PREFERENCE_KEYS`.
   */
  readonly telemetry: TelemetryPreference;
}

export type PreferenceKey = keyof TerminalPreferences;

/**
 * The persisted/synced envelope: values plus per-field write stamps. Stamps
 * are separate from the values so a field that was never touched is
 * distinguishable from one explicitly set to its default value — the former
 * must lose every merge, the latter must win against an older write.
 */
export interface PreferencesDoc {
  readonly version: 1;
  readonly values: TerminalPreferences;
  /** Per-field last-write time (ms epoch). Absent ⇒ never written here. */
  readonly stamps: Readonly<Partial<Record<PreferenceKey, number>>>;
}

export const DEFAULT_PREFERENCES: TerminalPreferences = {
  defaultModelId: null,
  theme: 'light',
  uiScale: 100,
  fontFamily: 'default',
  language: 'auto',
  // Attaching an account id is opted INTO, never out of.
  telemetry: 'anonymous',
};

export const EMPTY_PREFERENCES_DOC: PreferencesDoc = {
  version: 1,
  values: DEFAULT_PREFERENCES,
  stamps: {},
};

const THEMES: readonly ThemePreference[] = ['light', 'dark', 'system'];

const FONTS: readonly FontPreference[] = Object.keys(
  FONT_STACKS,
) as FontPreference[];

/** Every preference key, so merges and normalization can't miss a field. */
const PREFERENCE_KEYS: readonly PreferenceKey[] = [
  'defaultModelId',
  'theme',
  'uiScale',
  'fontFamily',
  'language',
  'telemetry',
];

/**
 * Preferences that stay on THIS DEVICE: persisted locally like any other, but
 * never written to — or read from — the `ui-state` sync document.
 *
 * `telemetry` is here because syncing it is unsafe until unknown-key
 * carry-through ships. Builds already in the field rebuild the sync doc from
 * their own `PREFERENCE_KEYS` (see the `TerminalUiStateDoc` comment in
 * `session-sync.ts`), so any key they don't know is dropped on read and the
 * doc is re-uploaded without it. Carry-through added here cannot fix that —
 * those builds are already deployed — so a synced tier could be silently
 * reset to the default by an older client. A device-local one cannot be.
 *
 * The cost is honest and bounded: the choice does not follow the user to a
 * new machine, where they get `anonymous` until they set it again. The
 * alternative is a choice that can be reverted without them knowing, which is
 * worse for the same reason a privacy setting exists at all.
 *
 * Flipping a key back to synced is a one-line change here, once carry-through
 * has rolled out.
 */
export const DEVICE_LOCAL_PREFERENCE_KEYS: readonly PreferenceKey[] = [
  'telemetry',
];

/** Whether `key` is excluded from the sync document. */
export function isDeviceLocalPreference(key: PreferenceKey): boolean {
  return DEVICE_LOCAL_PREFERENCE_KEYS.includes(key);
}

/**
 * The doc as it may go on the WIRE: device-local fields reset to their default
 * and their stamps dropped, so a local choice is neither uploaded nor left
 * looking like a write another device should adopt.
 *
 * Applied on BOTH sides of the sync boundary. Outbound is the privacy half;
 * inbound is the convergence half — an unstripped remote field would enter the
 * merge, differ from the stripped upload, and re-PUT on every launch.
 */
export function toSyncedPreferencesDoc(doc: PreferencesDoc): PreferencesDoc {
  const values: Record<string, unknown> = { ...doc.values };
  const stamps: Partial<Record<PreferenceKey, number>> = { ...doc.stamps };
  for (const key of DEVICE_LOCAL_PREFERENCE_KEYS) {
    values[key] = DEFAULT_PREFERENCES[key];
    delete stamps[key];
  }
  return {
    version: 1,
    values: values as unknown as TerminalPreferences,
    stamps,
  };
}

/**
 * A validator's verdict on one raw field.
 *
 * Deliberately NOT a plain `T | null` return. `defaultModelId`'s legitimate
 * "no explicit default" value IS null, so a null return read as "rejected" —
 * and since a rejected value forfeits its stamp, clearing the default to
 * "Gateway default" lost the stamp of a real user write. The clear then had no
 * authorship, another device's older pick outranked it, and that device
 * re-uploaded the model the user had just cleared: the clear never synced and
 * was actively reverted. `ok` separates "what value" from "was there one".
 */
type Verdict<T> =
  | { readonly ok: true; readonly value: T }
  // `fallback` overrides the field's DEFAULT for this rejection only. It
  // exists for a value that is present but unreadable, where the default is
  // the wrong safe answer — see `asTelemetry`. The stamp is still forfeited,
  // so a coerced value can never outrank the real one on merge.
  | { readonly ok: false; readonly fallback?: T };

const REJECTED = { ok: false } as const;

function accepted<T>(value: T): Verdict<T> {
  return { ok: true, value };
}

function asTheme(value: unknown): Verdict<ThemePreference> {
  return typeof value === 'string' &&
    (THEMES as readonly string[]).includes(value)
    ? accepted(value as ThemePreference)
    : REJECTED;
}

function asFont(value: unknown): Verdict<FontPreference> {
  return typeof value === 'string' &&
    (FONTS as readonly string[]).includes(value)
    ? accepted(value as FontPreference)
    : REJECTED;
}

function asLanguage(value: unknown): Verdict<LanguagePreference> {
  if (typeof value !== 'string') return REJECTED;
  // `hasOwnProperty`, NOT `in`: `in` walks the prototype chain, so
  // "constructor", "toString" and "__proto__" all passed as valid tags. The
  // lookup in `languageDirective` then returned a Function rather than
  // undefined and built "Respond in function Object() { [native code] }",
  // which rode the system preamble of every turn and round-tripped through
  // localStorage and the sync doc. Bounded to Object.prototype members on its
  // own, but it is a prototype-chain gadget: any other pollution primitive on
  // the page turns it into arbitrary preamble text.
  return value === 'auto' ||
    Object.prototype.hasOwnProperty.call(LANGUAGES, value)
    ? accepted(value as LanguagePreference)
    : REJECTED;
}

const TELEMETRY_TIERS: readonly TelemetryPreference[] = [
  'identified',
  'anonymous',
  'minimal',
];

/**
 * An unknown value falls back to the FLOOR, not the default: a doc this build
 * cannot read must not be taken as consent to report more than the user
 * agreed to.
 */
function asTelemetry(value: unknown): Verdict<TelemetryPreference> {
  if (
    typeof value === 'string' &&
    (TELEMETRY_TIERS as readonly string[]).includes(value)
  ) {
    return accepted(value as TelemetryPreference);
  }
  // Absent ⇒ never written, so take the default like any other field.
  if (value === undefined) return REJECTED;
  // PRESENT but unrecognised ⇒ a newer build's stricter tier read after a
  // rollback, or corruption. Sit at the FLOOR, not the default: `anonymous`
  // is the middle tier, so defaulting would report MORE than the user chose.
  // The stamp is forfeited like any other rejection, so the real value wins
  // again on the build that understands it.
  return { ok: false, fallback: 'minimal' };
}

function asUiScale(value: unknown): Verdict<UiScale> {
  // Membership, not a range check: an off-detent value (a hand-edited doc, a
  // build with a different ladder) would leave the slider with no matching
  // stop, so it is rejected rather than clamped.
  return typeof value === 'number' &&
    (UI_SCALES as readonly number[]).includes(value)
    ? accepted(value as UiScale)
    : REJECTED;
}

/**
 * A catalog model id, e.g. `dt-a/bedrock/anthropic/opus/5`.
 *
 * This is the only synced field whose value reaches the SOCKET: the seed
 * sends it as the `model` field of `sessions.patch`, and the catalog check is
 * skipped whenever the catalog is empty — routine on a cold connect, since
 * the fetch races the seed. The sync backend can be a different origin from
 * the chat gateway (`config.sidecar.baseUrl`), so "we wrote it" is not an
 * argument for trusting what comes back.
 *
 * The bound is a single-line token of bounded length: no whitespace, no
 * control/format characters, at most 128 characters. It was originally sized
 * for a sharper hazard — both seed sites INTERPOLATED this value into a
 * `/model <id>` chat message, so a planted newline made the client emit a
 * second slash command as the first thing every new chat sent. A JSON field
 * cannot be escaped that way, so the validator is now defence in depth
 * rather than the only thing standing between a foreign document and the
 * agent's command dispatcher. Kept as it is: a bad id still reaches a real
 * write, and the bound costs nothing.
 *
 * Deliberately no tighter: the settings picker
 * persists whatever id the live catalog serves, so an allowlist narrower than
 * the catalog's actual id space silently reverted a picked model on the next
 * load. And deliberately not a catalog lookup: an id legitimately outlives
 * its catalog entry, and that case is handled downstream.
 */
const MODEL_ID = /^[^\s\p{C}]{1,128}$/u;

/**
 * UI sentinel for "Gateway default" in the settings picker — a menu value
 * must be a string, while the STORED representation of that choice is `null`.
 * Defined here, beside the validator that must refuse it: the sentinel fits
 * the id charset, so without the explicit check a foreign doc could plant it,
 * where it would render as an orphaned saved default and be written to the
 * gateway as `sessions.patch { model: "__gateway_default__" }` on a cold
 * connect. The picker converts it to `null` at onChange; it must never
 * persist.
 */
export const GATEWAY_DEFAULT_SENTINEL = '__gateway_default__';

function asModelId(value: unknown): Verdict<string | null> {
  // `null` is this field's way of saying "use whatever the gateway defaults
  // to" — a choice the user makes explicitly by picking "Gateway default".
  // It is a VALUE, so it is accepted and keeps its stamp; only `undefined`
  // (the key absent entirely) means never-written.
  if (value === null) return accepted(null);
  if (value === GATEWAY_DEFAULT_SENTINEL) return REJECTED;
  return typeof value === 'string' && MODEL_ID.test(value)
    ? accepted(value)
    : REJECTED;
}

/**
 * How far ahead of this device's clock a stamp may sit before it is treated as
 * skew rather than a real write. Five minutes is generous for genuine drift
 * and far short of the "pins the field forever" range.
 */
const STAMP_SKEW_ALLOWANCE_MS = 5 * 60 * 1000;

function asStamp(value: unknown): number | null {
  // Reject NaN/Infinity/zero/negatives — and CLAMP the future, which the old
  // finite-check missed entirely. A device with a clock set ahead (or a
  // corrupt synced doc) writes a far-future stamp; every correctly-clocked
  // device then stamps with `Date.now()`, loses the comparison, and the user's
  // click silently reverts on the next hydrate. Not "one re-click" as the
  // module header promises — permanent, until someone clears storage.
  //
  // Zero is rejected, not accepted: every other layer already reads 0 as
  // "absent" (`?? 0` in the merge and the equality check; `stamp > 0` gates
  // what the merge stores), so accepting it here made normalize's output not
  // closed under the merge for exactly one foreign shape — a zero-stamped
  // field round-tripped as "changed" on every launch and re-queued an upload
  // forever. No build writes a zero stamp (`setPreference` emits
  // `max(now, previous + 1) ≥ 1`), so nothing legitimate is lost.
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return Math.min(value, Date.now() + STAMP_SKEW_ALLOWANCE_MS);
}

/**
 * Whether a RAW doc carries a stamp this build had to clamp.
 *
 * The clamp itself only repairs the copy in memory, and the hydration upload
 * guard compares the merge against the ALREADY-NORMALIZED remote — so a
 * clamped stamp looked like no difference at all and the repair was never sent
 * back. The gateway kept the poisoned value, its clamp ceiling advanced with
 * wall-clock on every read, and the local write only stayed ahead while its
 * PUT could actually go out: offline, or unauthenticated, the field re-pinned
 * on every hydrate and the user's click kept reverting.
 *
 * Deliberately narrow — stamps only. A REJECTED VALUE is also repaired on
 * read, but writing that back would be wrong: an old build cannot tell a
 * corrupt value from one a newer build understands, so it would helpfully
 * overwrite the newer build's setting with its own default. A far-future
 * stamp needs no such judgement; it is wrong on any build.
 */
export function hasClampedStamp(value: unknown): boolean {
  if (value == null || typeof value !== 'object') return false;
  const stamps = (value as Record<string, unknown>).stamps;
  if (stamps == null || typeof stamps !== 'object') return false;
  const ceiling = Date.now() + STAMP_SKEW_ALLOWANCE_MS;
  return PREFERENCE_KEYS.some((key) => {
    const raw = (stamps as Record<string, unknown>)[key];
    return typeof raw === 'number' && Number.isFinite(raw) && raw > ceiling;
  });
}

/**
 * Coerce anything — a localStorage blob, a doc from another device, a doc
 * written by a newer build — into a valid `PreferencesDoc`. Unknown fields are
 * dropped and invalid ones fall back to their default, so a single bad value
 * can't take the whole settings surface down.
 */
export function normalizePreferencesDoc(value: unknown): PreferencesDoc {
  if (value == null || typeof value !== 'object') return EMPTY_PREFERENCES_DOC;
  const obj = value as Record<string, unknown>;
  const rawValues = (
    obj.values != null && typeof obj.values === 'object' ? obj.values : {}
  ) as Record<string, unknown>;
  const rawStamps = (
    obj.stamps != null && typeof obj.stamps === 'object' ? obj.stamps : {}
  ) as Record<string, unknown>;

  const validators: {
    [K in PreferenceKey]: (raw: unknown) => Verdict<TerminalPreferences[K]>;
  } = {
    defaultModelId: asModelId,
    theme: asTheme,
    uiScale: asUiScale,
    fontFamily: asFont,
    language: asLanguage,
    telemetry: asTelemetry,
  };

  const values: Record<string, unknown> = {};
  const stamps: Partial<Record<PreferenceKey, number>> = {};

  for (const key of PREFERENCE_KEYS) {
    const verdict = validators[key](rawValues[key]);
    // A value and its stamp are one WRITE, and each is meaningless alone —
    // so the pair survives together or falls back to the default together.
    //
    // A rejected VALUE forfeits its stamp: validating the two independently
    // let an old build coerce a newer build's value to the default while
    // keeping the newer build's timestamp — and that coerced default then
    // outranked, or tied and defeated, the real value on merge. A timestamp
    // only means something attached to the write it describes.
    //
    // A missing (or rejected) STAMP forfeits its value, for the mirror-image
    // reason: an accepted value with no stamp entered every merge at 0, where
    // it lost to any real write and fell to an arbitrary tie-break against
    // the untouched default — the doc SAID `theme: dark` and the app came up
    // however the JSON strings happened to sort. No build of this app writes
    // a value unstamped (`setPreference` stamps every write), so the state
    // only arrives from outside; treating it as never-written keeps the
    // merge's inputs meaning what they claim.
    //
    // Keyed on `ok`, never on the value being null — see `Verdict`.
    const stamp = verdict.ok ? asStamp(rawStamps[key]) : null;
    if (verdict.ok && stamp != null) {
      values[key] = verdict.value;
      stamps[key] = stamp;
    } else {
      // A rejection may name its own safe value; otherwise the field default.
      values[key] =
        (verdict.ok ? undefined : verdict.fallback) ?? DEFAULT_PREFERENCES[key];
    }
  }

  return {
    version: 1,
    values: values as unknown as TerminalPreferences,
    stamps,
  };
}

/**
 * Write one preference, stamping just that field. `now` is injected so the
 * caller controls the clock (and tests stay deterministic).
 */
export function setPreference<K extends PreferenceKey>(
  doc: PreferencesDoc,
  key: K,
  value: TerminalPreferences[K],
  now: number,
): PreferencesDoc {
  // Monotonic per field. `now` is normally the newest stamp anyway, but if a
  // skewed device or a corrupt doc left a stamp in the future, a wall-clock
  // write would lose to it — and keep losing, so the control would appear
  // dead. Stepping past the existing stamp guarantees the user's own click
  // wins on their own device, whatever the stored value claims.
  const previous = doc.stamps[key] ?? 0;
  return {
    version: 1,
    values: { ...doc.values, [key]: value },
    stamps: { ...doc.stamps, [key]: Math.max(now, previous + 1) },
  };
}

/**
 * Field-level last-write-wins. Ties go to `local` — a tie needs two writes in
 * the same millisecond, and resolving it locally keeps the merge deterministic
 * (and idempotent, so re-merging the same pair never flips a value).
 */
export function mergePreferences(
  local: PreferencesDoc,
  remote: PreferencesDoc,
): PreferencesDoc {
  const values: Record<string, unknown> = {};
  const stamps: Partial<Record<PreferenceKey, number>> = {};

  for (const key of PREFERENCE_KEYS) {
    const localStamp = local.stamps[key] ?? 0;
    const remoteStamp = remote.stamps[key] ?? 0;
    // A device-local field the other side never wrote is not a contender.
    //
    // Stamp LWW alone left this to the tie-break below, which orders by
    // `JSON.stringify` — so whether the floor `asTelemetry` coerces to
    // survived a merge against a stripped doc depended on how the tier's NAME
    // sorted. Correct today by alphabet, not by rule. Skipping outright is
    // the rule: `toSyncedPreferencesDoc` drops the stamp, so an unstamped
    // side is one that had nothing to say about this field.
    //
    // Deliberately keyed on the OTHER side's stamp rather than "local always
    // wins": both arguments are local documents on the disk∪live merge, and
    // there the live copy legitimately holds a stamped edit a failed
    // `store.save` never persisted.
    if (isDeviceLocalPreference(key) && remoteStamp === 0) {
      values[key] = local.values[key];
      if (localStamp > 0) stamps[key] = localStamp;
      continue;
    }
    const takeRemote =
      remoteStamp !== localStamp
        ? remoteStamp > localStamp
        : // Same stamp, different values: break the tie on the VALUE, so both
          // devices compute the same winner. Preferring "local" here read as
          // harmless but made the merge non-commutative — each side kept its
          // own value, each saw the merge differ from remote, and each
          // re-uploaded on every launch, alternating forever without ever
          // converging. Any total order works; this one is stable and needs no
          // extra state.
          JSON.stringify(remote.values[key] ?? null) >
          JSON.stringify(local.values[key] ?? null);
    values[key] = takeRemote ? remote.values[key] : local.values[key];
    const stamp = takeRemote ? remoteStamp : localStamp;
    if (stamp > 0) stamps[key] = stamp;
  }

  return {
    version: 1,
    values: values as unknown as TerminalPreferences,
    stamps,
  };
}

/** True when two docs carry the same values AND the same stamps. */
export function preferencesDocsEqual(
  a: PreferencesDoc,
  b: PreferencesDoc,
): boolean {
  return PREFERENCE_KEYS.every(
    (key) =>
      a.values[key] === b.values[key] &&
      (a.stamps[key] ?? 0) === (b.stamps[key] ?? 0),
  );
}

/**
 * Deliberately no `clear`: preferences are per-account synced state that, like
 * chat history, survives sign-out and re-hydrates from the gateway on the next
 * sign-in — a local wipe would be undone by the first hydrate. A real
 * account-deletion flow needs a gateway-side wipe first, and can add the
 * local half when it exists.
 */
export interface PreferencesStore {
  load(accountId: string): PreferencesDoc;
  save(accountId: string, doc: PreferencesDoc): void;
}

export interface PreferencesStoreConfig {
  readonly keyFor: (
    scope: string,
    accountId: string,
    ...extra: string[]
  ) => string;
}

/**
 * localStorage-backed preferences store. Reads never throw (private mode, SSR,
 * quota) — a failed read is indistinguishable from "nothing saved yet", which
 * is the correct fallback for preferences.
 */
export function createPreferencesStore(
  config: PreferencesStoreConfig,
): PreferencesStore {
  const { keyFor } = config;

  return {
    load(accountId: string): PreferencesDoc {
      try {
        const key = keyFor('preferences', accountId);
        const raw = localStorage.getItem(key);
        if (raw == null || raw.length === 0) return EMPTY_PREFERENCES_DOC;
        const parsed: unknown = JSON.parse(raw);
        const doc = normalizePreferencesDoc(parsed);

        // Heal a poisoned stamp on the way past, rather than re-clamping the
        // same bad value on every read forever.
        //
        // The clamp is relative to `Date.now()`, so an unrepaired local doc
        // produces a slightly HIGHER stamp each launch — which then beats the
        // repaired copy already on the gateway, gets uploaded over it, and
        // burns a rev per launch for as long as the device lives. Repairing
        // the remote alone could not close that: this copy kept re-poisoning
        // it.
        //
        // Everything not being repaired is preserved, and that has to include
        // STAMPS, not just values. `doc.stamps` enumerates only the keys this
        // build knows, so assigning it wholesale deleted a newer build's
        // timestamp while carefully keeping its value — and a value with no
        // stamp loses every field-level merge, so that is the same data loss
        // one merge later, by a slower route. Raw first, repaired known keys
        // over the top.
        //
        // A known key whose stamp normalize REJECTED outright (NaN, negative)
        // is absent from `doc.stamps` and so keeps its raw value here. That is
        // deliberate: it leaves the field exactly as it was before this repair
        // existed, and normalize drops it on every read regardless — the same
        // reason a rejected VALUE is never written back either.
        if (hasClampedStamp(parsed)) {
          try {
            const rawStamps = (parsed as { readonly stamps?: unknown }).stamps;
            const stamps: Record<string, unknown> = {
              ...(typeof rawStamps === 'object' && rawStamps != null
                ? rawStamps
                : {}),
              ...doc.stamps,
            };
            // A key whose VALUE was rejected has no entry in `doc.stamps`, so
            // the overlay above leaves its raw far-future stamp in place —
            // and `hasClampedStamp` kept seeing it, so this write-back
            // re-fired on every load forever. Clamp those stamps here too.
            // This does NOT revive the stamp for merging — normalize drops
            // the rejected pair on every read regardless — it just makes the
            // stored doc stop tripping the detector.
            const ceiling = Date.now() + STAMP_SKEW_ALLOWANCE_MS;
            for (const k of PREFERENCE_KEYS) {
              const v = stamps[k];
              if (typeof v === 'number' && Number.isFinite(v) && v > ceiling) {
                stamps[k] = ceiling;
              }
            }
            const repaired = { ...(parsed as object), stamps };
            localStorage.setItem(key, JSON.stringify(repaired));
          } catch {
            // best-effort: the clamped doc still drives this session.
          }
        }
        return doc;
      } catch {
        return EMPTY_PREFERENCES_DOC;
      }
    },
    save(accountId: string, doc: PreferencesDoc): void {
      try {
        localStorage.setItem(
          keyFor('preferences', accountId),
          JSON.stringify(doc),
        );
      } catch {
        // best-effort: the in-memory value still drives this session.
      }
    },
  };
}
